// src/components/quickMessage/HomeSection.tsx -- orchestrator for the
// recipient-side Quick Message surface on the home page.
//
// One fetch on mount. One 30-second ticker that re-renders so the
// `timeSinceLabel()` strings refresh without re-fetching (gotcha #11).
// PATCH `readAt` fires the first time the user expands a message; later
// expansions are no-ops (gotcha #3).
//
// Banner vs pill threshold: < 2 inbox rows -> single Banner, >= 2 -> MultiPill
// (see plan pack and gotcha #4). After Dismiss or Reply, the inbox is
// re-fetched so the count corrects itself; this also catches anything the
// server changed (e.g. an atomic reply-then-dismiss flow).
//
// On any write that changes the unread count (PATCH readAt, dismiss, reply),
// we call `onUnreadChanged` so the parent can refresh `/api/me` for the
// bottom-nav dot. The parent decides whether that refresh is immediate or
// debounced -- HomeSection just signals.

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../api";
import { C, F } from "../../theme";
import {
  fetchInbox,
  fetchThread,
  patchQuickMessage,
  sendQuickMessage,
  type QuickMessage,
  type ThreadMessage,
} from "../../lib/quickMessages";
import { Banner } from "./Banner";
import { MultiPill } from "./MultiPill";

interface ExpandedState {
  messageId: string;
  threadOpen: boolean;
  thread: ThreadMessage[] | null;
  threadLoading: boolean;
  threadError: string | null;
}

export interface QuickMessageHomeSectionProps {
  /** Called after any write that changes the user's unread count. */
  onUnreadChanged: () => void;
}

export function QuickMessageHomeSection({
  onUnreadChanged,
}: QuickMessageHomeSectionProps) {
  const [messages, setMessages] = useState<QuickMessage[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [expanded, setExpanded] = useState<ExpandedState | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Track which messages have had their readAt PATCHed during this session
  // so we don't duplicate the fire-and-forget call on every re-expand.
  const readPatchedRef = useRef<Set<string>>(new Set());

  // -------------------------------------------------------------------------
  // Fetch + ticker
  // -------------------------------------------------------------------------

  const refetch = useCallback(async () => {
    try {
      const res = await fetchInbox();
      setMessages(res.messages);
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : "Couldn’t load messages.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchInbox();
        if (!cancelled) {
          setMessages(res.messages);
          setLoadError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(
            e instanceof ApiError ? e.message : "Couldn’t load messages.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 30s ticker -- no refetch, just bumps state to re-derive time-since labels.
  useEffect(() => {
    const id = window.setInterval(() => {
      setTick((t) => t + 1);
    }, 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(id);
  }, [toast]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const onExpand = useCallback(
    (m: QuickMessage) => {
      setExpanded({
        messageId: m.id,
        threadOpen: false,
        thread: null,
        threadLoading: false,
        threadError: null,
      });
      // First-expand readAt PATCH (gotcha #3). Fire-and-forget; don't block UI.
      if (m.readAt === null && !readPatchedRef.current.has(m.id)) {
        readPatchedRef.current.add(m.id);
        patchQuickMessage(m.id, { readAt: true })
          .then(() => {
            setMessages((prev) =>
              prev
                ? prev.map((x) =>
                    x.id === m.id
                      ? { ...x, readAt: new Date().toISOString() }
                      : x,
                  )
                : prev,
            );
            onUnreadChanged();
          })
          .catch(() => {
            // Swallow -- the read state is only a courtesy. If it fails we
            // try again on next expand (the ref's entry isn't cleared, but
            // the next visit will re-fetch and the readAt will reflect
            // whatever the server has).
            readPatchedRef.current.delete(m.id);
          });
      }
    },
    [onUnreadChanged],
  );

  const onCollapse = useCallback(() => {
    setExpanded(null);
  }, []);

  const onToggleThread = useCallback(() => {
    setExpanded((prev) => {
      if (!prev) return prev;
      const willOpen = !prev.threadOpen;
      if (willOpen && prev.thread === null && !prev.threadLoading) {
        const id = prev.messageId;
        // Kick off the lazy fetch.
        fetchThread(id)
          .then((res) => {
            setExpanded((curr) =>
              curr && curr.messageId === id
                ? { ...curr, thread: res.messages, threadLoading: false }
                : curr,
            );
          })
          .catch((e) => {
            setExpanded((curr) =>
              curr && curr.messageId === id
                ? {
                    ...curr,
                    threadLoading: false,
                    threadError:
                      e instanceof ApiError ? e.message : "Couldn’t load thread.",
                  }
                : curr,
            );
          });
        return { ...prev, threadOpen: true, threadLoading: true, threadError: null };
      }
      return { ...prev, threadOpen: willOpen };
    });
  }, []);

  const onDismiss = useCallback(
    async (m: QuickMessage) => {
      if (busy) return;
      setBusy(true);
      // Optimistic: remove from local list immediately.
      const previous = messages;
      setMessages((prev) => (prev ? prev.filter((x) => x.id !== m.id) : prev));
      setExpanded(null);
      try {
        await patchQuickMessage(m.id, { dismissedAt: true });
        onUnreadChanged();
      } catch (e) {
        // Revert
        setMessages(previous);
        setToast(
          e instanceof ApiError ? e.message : "Couldn’t dismiss. Try again.",
        );
      } finally {
        setBusy(false);
      }
    },
    [busy, messages, onUnreadChanged],
  );

  const onSendReply = useCallback(
    async (m: QuickMessage, body: string) => {
      if (busy) return;
      if (m.sender.id === null) return; // orphan; reply is disabled anyway
      setBusy(true);
      const previous = messages;
      // Optimistic: the API atomically dismisses the original on reply, so
      // remove it from local state.
      setMessages((prev) => (prev ? prev.filter((x) => x.id !== m.id) : prev));
      setExpanded(null);
      try {
        await sendQuickMessage({
          recipientId: m.sender.id,
          body,
          replyToId: m.id,
        });
        setToast(`Reply sent to ${m.sender.firstName}.`);
        onUnreadChanged();
      } catch (e) {
        // Revert
        setMessages(previous);
        setToast(
          e instanceof ApiError ? e.message : "Couldn’t send reply. Try again.",
        );
        // Refetch as a safety net in case the server actually accepted it.
        refetch();
      } finally {
        setBusy(false);
      }
    },
    [busy, messages, onUnreadChanged, refetch],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loadError) {
    return (
      <div role="status" style={errorBoxStyle}>
        {loadError}
      </div>
    );
  }

  // Loading state -- render nothing rather than a skeleton, to avoid layout
  // shift when the typical "0 unread" arrives.
  if (messages === null) return toast ? <Toast text={toast} /> : null;

  if (messages.length === 0) return toast ? <Toast text={toast} /> : null;

  // -------------------------------------------------------------------------
  // Single banner (1 inbox row)
  // -------------------------------------------------------------------------

  if (messages.length === 1) {
    const m = messages[0];
    const isExpanded = expanded?.messageId === m.id;
    return (
      <>
        <Banner
          message={m}
          expanded={isExpanded}
          tick={tick}
          thread={isExpanded ? expanded?.thread ?? null : null}
          threadOpen={isExpanded ? expanded?.threadOpen ?? false : false}
          threadLoading={isExpanded ? expanded?.threadLoading ?? false : false}
          threadError={isExpanded ? expanded?.threadError ?? null : null}
          busy={busy}
          onExpand={() => onExpand(m)}
          onCollapse={onCollapse}
          onToggleThread={onToggleThread}
          onDismiss={() => onDismiss(m)}
          onSendReply={(body) => onSendReply(m, body)}
        />
        {toast && <Toast text={toast} />}
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Multi pill (>= 2 inbox rows)
  // -------------------------------------------------------------------------

  return (
    <>
      <MultiPill
        messages={messages}
        tick={tick}
        busy={busy}
        thread={expanded?.thread ?? null}
        threadOpen={expanded?.threadOpen ?? false}
        threadLoading={expanded?.threadLoading ?? false}
        threadError={expanded?.threadError ?? null}
        expandedId={expanded?.messageId ?? null}
        onExpand={onExpand}
        onCollapse={onCollapse}
        onToggleThread={onToggleThread}
        onDismiss={onDismiss}
        onSendReply={onSendReply}
      />
      {toast && <Toast text={toast} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function Toast({ text }: { text: string }) {
  return (
    <div role="status" style={toastStyle}>
      {text}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const errorBoxStyle = {
  padding: "10px 12px",
  background: C.signalBg,
  border: "1px solid rgba(184,58,43,0.22)",
  borderRadius: 10,
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 700,
  color: C.signalDk,
};

const toastStyle = {
  marginTop: 12,
  padding: "12px 14px",
  borderRadius: 12,
  background: C.sageBg,
  border: `1px solid ${C.sageLt}`,
  color: C.sageDk,
  fontFamily: F.body,
  fontWeight: 800,
  fontSize: 13,
  letterSpacing: 0.3,
};
